import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  FileDownload as ExcelIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';

const AAFeeInvoiceGenerate = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    transactionType: '',
    orderId: '',
    labType: '',
    userName: '',
    financialYear: '25',
    dateFrom: null,
    dateTo: null,
  });

  // Dropdown options
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [labTypes, setLabTypes] = useState([]);

  const financialYears = [
    { value: '25', label: '2025-2026' },
    { value: '24', label: '2024-2025' },
    { value: '23', label: '2023-2024' },
    { value: '22', label: '2022-2023' },
    { value: '21', label: '2021-2022' },
    { value: '20', label: '2020-2021' },
  ];

  // Error and success states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
    fetchInvoiceData();
  }, []);

  // Load dropdown data
  const loadInitialData = async () => {
    try {
      // Load payment types (only AA Fee - ID 7)
      const paymentTypesResponse = await fetch(`${process.env.REACT_APP_API_URL}/aafee/payment-types`);
      if (paymentTypesResponse.ok) {
        const paymentTypesData = await paymentTypesResponse.json();
        if (paymentTypesData.success) {
          setTransactionTypes(paymentTypesData.data);
        }
      }

      // Load lab types from common function API
      const labTypesResponse = await fetch(`${process.env.REACT_APP_API_URL}/common/lab-types`);
      if (labTypesResponse.ok) {
        const labTypesData = await labTypesResponse.json();
        if (labTypesData.success) {
          setLabTypes(labTypesData.data);
        }
      } else {
        // Mock lab types as fallback
        setLabTypes([
          { id: '1', name: 'Testing Laboratory' },
          { id: '2', name: 'Calibration Laboratory' },
          { id: '3', name: 'Medical Laboratory' },
        ]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load initial data');
      
      // Set fallback data
      setTransactionTypes([
        { id: '7', description: 'AA Fee' }
      ]);
      setLabTypes([
        { id: '1', name: 'Testing Laboratory' },
        { id: '2', name: 'Calibration Laboratory' },
        { id: '3', name: 'Medical Laboratory' },
      ]);
    }
  };

  // Fetch invoice data with filters
  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    try {
      // Build filter conditions
      const conditions = {
        paymentTypeId: 7, // AA Fee
        orderStatus: 'Success',
        invoiceGenerated: true,
        transactionType: filters.transactionType,
        orderId: filters.orderId,
        labType: filters.labType,
        userName: filters.userName,
        financialYear: filters.financialYear,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        page: page + 1,
        pageSize: rowsPerPage,
      };

      // Remove empty filters
      Object.keys(conditions).forEach(key => {
        if (conditions[key] === '' || conditions[key] === null) {
          delete conditions[key];
        }
      });

      // API call to fetch invoice data
      const response = await fetch(`${process.env.REACT_APP_API_URL}/aafee/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(conditions),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data || []);
          setFilteredData(result.data || []);
          setTotalRecords(result.totalRecords || 0);
        } else {
          throw new Error(result.message || 'Failed to fetch data');
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

    } catch (error) {
      console.error('Error fetching invoice data:', error);
      setError('Failed to fetch invoice data: ' + error.message);
      
      // Use mock data as fallback
      const mockData = generateMockData();
      setData(mockData.data);
      setFilteredData(mockData.data);
      setTotalRecords(mockData.total);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Generate mock data
  const generateMockData = () => {
    const mockInvoices = [];
    for (let i = 1; i <= 50; i++) {
      mockInvoices.push({
        id: i,
        invoiceNo: `AAFEE/${String(i).padStart(6, '0')}`,
        invoiceDate: new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
        labId: `LAB${String(i).padStart(4, '0')}`,
        laboratoryName: `Laboratory ${i}`,
        orderId: `ORD${String(i).padStart(8, '0')}`,
        feesType: 'AA Fee',
        paymentStatus: 'Success',
        paymentMode: ['Online', 'Offline', 'NEFT', 'RTGS'][Math.floor(Math.random() * 4)],
        transactionDate: new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
        userName: `user${i}@example.com`,
        labType: ['Testing Laboratory', 'Calibration Laboratory', 'Medical Laboratory'][Math.floor(Math.random() * 3)],
        contactPerson: `Contact Person ${i}`,
        email: `contact${i}@lab.com`,
        mobile: `+91 98765${String(i).padStart(5, '0')}`,
        address: `Address ${i}, City ${i}`,
        state: `State ${i % 10 + 1}`,
        city: `City ${i % 20 + 1}`,
        pincode: `${String(i % 999999).padStart(6, '0')}`,
        invoicePath: `/invoices/aafee/${i}.pdf`,
      });
    }
    return {
      data: mockInvoices.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
      total: mockInvoices.length,
    };
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchInvoiceData();
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      transactionType: '',
      orderId: '',
      labType: '',
      userName: '',
      financialYear: '25',
      dateFrom: null,
      dateTo: null,
    });
    setPage(0);
    setTimeout(() => fetchInvoiceData(), 100);
  };

  // Handle Excel download
  const handleExcelDownload = async () => {
    try {
      setLoading(true);
      
      // Prepare filters for export
      const exportFilters = {
        transactionType: filters.transactionType,
        orderId: filters.orderId,
        labType: filters.labType,
        userName: filters.userName,
        financialYear: filters.financialYear,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      };

      // Remove empty filters
      Object.keys(exportFilters).forEach(key => {
        if (exportFilters[key] === '' || exportFilters[key] === null) {
          delete exportFilters[key];
        }
      });

      // API call to download Excel
      const response = await fetch(`${process.env.REACT_APP_API_URL}/aafee/invoices/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(exportFilters),
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'AA_Fee_Invoice_Report.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('Excel file downloaded successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download Excel file');
      }

    } catch (error) {
      console.error('Error downloading Excel:', error);
      setError('Failed to download Excel file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Refetch data when page or rowsPerPage changes
  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            AA Fee Invoice Generate
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            View and manage AA Fee invoices with comprehensive filtering and export options
          </Typography>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{ mr: 1 }}
                startIcon={<RefreshIcon />}
              >
                Reset Filter
              </Button>
              <Button
                variant="contained"
                onClick={handleExcelDownload}
                startIcon={<ExcelIcon />}
                disabled={loading}
              >
                Download Excel
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={filters.transactionType}
                  label="Transaction Type"
                  onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                >
                  <MenuItem value="">All Transaction Types</MenuItem>
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Order ID"
                placeholder="Enter Order ID"
                value={filters.orderId}
                onChange={(e) => handleFilterChange('orderId', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Lab Type</InputLabel>
                <Select
                  value={filters.labType}
                  label="Lab Type"
                  onChange={(e) => handleFilterChange('labType', e.target.value)}
                >
                  <MenuItem value="">All Lab Types</MenuItem>
                  {labTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="User Name"
                placeholder="Enter User Name"
                value={filters.userName}
                onChange={(e) => handleFilterChange('userName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Financial Year</InputLabel>
                <Select
                  value={filters.financialYear}
                  label="Financial Year"
                  onChange={(e) => handleFilterChange('financialYear', e.target.value)}
                >
                  {financialYears.map((year) => (
                    <MenuItem key={year.value} value={year.value}>
                      {year.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Date From"
                value={filters.dateFrom}
                onChange={(date) => handleFilterChange('dateFrom', date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={new Date()}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Date To"
                value={filters.dateTo}
                onChange={(date) => handleFilterChange('dateTo', date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={new Date()}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ height: '56px' }}
                disabled={loading}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Data Table */}
        <Paper>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>S. No</TableCell>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Invoice Generated Date</TableCell>
                  <TableCell>Lab ID</TableCell>
                  <TableCell>Laboratory Name</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Fees Type</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Payment Mode</TableCell>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>User Name</TableCell>
                  <TableCell>Lab Type</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Pincode</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={20} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} align="center">
                      <Typography variant="h6" color="text.secondary">
                        No Data Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            component={Link}
                            href={row.invoicePath}
                            target="_blank"
                            color="primary"
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.invoiceNo}</TableCell>
                      <TableCell>
                        {format(row.invoiceDate, 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>{row.labId}</TableCell>
                      <TableCell>{row.laboratoryName}</TableCell>
                      <TableCell>{row.orderId}</TableCell>
                      <TableCell>{row.feesType}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.paymentStatus}
                          color={row.paymentStatus === 'Success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{row.paymentMode}</TableCell>
                      <TableCell>
                        {format(row.transactionDate, 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>{row.userName}</TableCell>
                      <TableCell>{row.labType}</TableCell>
                      <TableCell>{row.contactPerson}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.mobile}</TableCell>
                      <TableCell>{row.address}</TableCell>
                      <TableCell>{row.state}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell>{row.pincode}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default AAFeeInvoiceGenerate;